import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import DocumentPreview from './DocumentPreview';

const stringifyDocument = (value) => JSON.stringify(value ?? {}, null, 2);

const LegalDocumentEditor = ({
  permissionName,
  pageTitle,
  pageDescription,
  saveSuccessMessage,
  loadDocument,
  saveDocument,
  defaultDocument,
  defaultPartnerDocument,
  supportsPartnerDocument = false,
}) => {
  usePermissions(permissionName);

  const [documentJson, setDocumentJson] = useState(stringifyDocument(defaultDocument));
  const [partnerDocumentJson, setPartnerDocumentJson] = useState(
    stringifyDocument(defaultPartnerDocument)
  );
  const [activeTab, setActiveTab] = useState('user');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [livePreview, setLivePreview] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const activeJson = activeTab === 'partner' ? partnerDocumentJson : documentJson;
  const setActiveJson = activeTab === 'partner' ? setPartnerDocumentJson : setDocumentJson;

  useEffect(() => {
    reloadDocument();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const parsed = JSON.parse(activeJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setLivePreview(null);
          setPreviewError('Preview requires a JSON object');
          return;
        }
        setLivePreview(parsed);
        setPreviewError('');
      } catch (_) {
        setLivePreview(null);
        setPreviewError('Invalid JSON — fix syntax to update preview');
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [activeJson]);

  const reloadDocument = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await loadDocument();
      if (!result.success) {
        setError(result.error);
        return;
      }

      if (result.data?.document) {
        setDocumentJson(stringifyDocument(result.data.document));
      } else {
        setDocumentJson(stringifyDocument(defaultDocument));
      }

      if (supportsPartnerDocument) {
        if (result.data?.partnerDocument) {
          setPartnerDocumentJson(stringifyDocument(result.data.partnerDocument));
        } else {
          setPartnerDocumentJson(stringifyDocument(defaultPartnerDocument));
        }
      }

      setLastSaved(result.data?.lastUpdated ? new Date(result.data.lastUpdated) : null);
    } catch (_) {
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const parseJson = (jsonText, label) => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(`${label} must be a JSON object`);
      }
      if (!parsed.title || !String(parsed.title).trim()) {
        throw new Error(`${label} must include a non-empty "title" field`);
      }
      return parsed;
    } catch (err) {
      throw new Error(err.message || `Invalid ${label} JSON`);
    }
  };

  const formatJson = (jsonText, setter) => {
    try {
      const parsed = JSON.parse(jsonText);
      setter(stringifyDocument(parsed));
      setError('');
    } catch (_) {
      setError('Fix JSON syntax before formatting');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const document = parseJson(documentJson, 'Document');
      const payload = {
        document,
        mainHeading: document.title,
      };

      if (supportsPartnerDocument) {
        payload.partnerDocument = parseJson(partnerDocumentJson, 'Partner document');
      }

      const result = await saveDocument(payload);
      if (!result.success) {
        setError(result.error);
        return;
      }

      setSaved(true);
      setLastSaved(result.data?.lastUpdated ? new Date(result.data.lastUpdated) : new Date());
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{pageTitle}</h1>
          <p className="text-gray-600">{pageDescription}</p>
          {lastSaved && (
            <p className="text-sm text-gray-500 mt-2">
              Last saved: {lastSaved.toLocaleString()}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {saved && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="bg-green-500 text-white px-8 py-4 rounded-lg shadow-lg text-lg font-semibold pointer-events-auto">
              {saveSuccessMessage}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Document JSON</h2>
                  <p className="text-sm text-gray-600">
                    Edit the full document object returned by the public API.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => formatJson(activeJson, setActiveJson)}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Format JSON
                </button>
              </div>

              {supportsPartnerDocument && (
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('user')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      activeTab === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    User Document
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('partner')}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      activeTab === 'partner'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Partner Document
                  </button>
                </div>
              )}
            </div>

            <div className="p-6">
              <textarea
                value={activeJson}
                onChange={(e) => setActiveJson(e.target.value)}
                className="w-full h-[640px] font-mono text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Live Preview</h2>
              <p className="text-sm text-gray-600">
                Updates as you edit the JSON on the left.
              </p>
            </div>
            <div className="p-6">
              {previewError ? (
                <p className="text-amber-700 text-sm mb-4">{previewError}</p>
              ) : null}
              <DocumentPreview document={livePreview} />
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={reloadDocument}
              disabled={loading}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentEditor;
